// TRIL-608: the CANONICAL blocks → analysed-text rule, shared by server, web and mobile.
//
// TRIL-603 shared how to COMPARE two analysed strings (normalizeStoryText) but left how to
// BUILD one duplicated three ways. That is only half a rule: shouldReanalyzeOnEdit(current,
// stored) is meaningless unless both sides came from the same producer, and TRIL-604 stores
// the string as sent. The three copies had diverged on four of six block types (TRIL-607),
// and one of the divergences was a live safety hole (TRIL-609).
//
// THE PRINCIPLE: every authored character appears exactly once; nothing synthetic is added.
//
// It follows from what this string actually feeds:
//   * the pre-publish SAFETY gate (TRIL-247/274, STYZER-53) — any omission is a bypass path,
//     so completeness is not negotiable. This is what TRIL-609 got wrong: web read Quote
//     fields nothing writes, so quoted content reached neither scoring nor the safety gate.
//   * the MIN-LENGTH publish gate — it counts characters, so synthetic tokens are padding the
//     author did not write (`[Code block]` contributed 12 characters for an EMPTY block).
//   * QUALITY scoring — bullets and `[Code block: …]` wrappers are tokens no author typed.
//   * TRIL-604 staleness — indifferent to content, needs only determinism.
//
// HOW CROSS-TIER AGREEMENT IS ENFORCED: structurally, not by assertion. All three tiers import
// THIS function, so there is nothing left to diverge — no test compares two implementations
// because there is only one. That is stronger than an equality test, but it does mean the guard
// is "don't reintroduce a local copy", which review has to catch rather than CI.
//
// Known cost, accepted deliberately: raw code in the prose stream distorts readability metrics
// (Flesch over source code is meaningless). Truncating it — the old behaviour — hid everything
// past 100 characters from the safety gate, which is the same class of hole as TRIL-609. A
// visible scoring distortion beats an invisible bypass. The real fix is a STRUCTURED analyzer
// payload so prose and code can be scored differently while both are safety-scanned; that is an
// analyzer-contract change (STYZER), not something a flat string can express.

/** Structural shape only — deliberately not any platform's TextBlock type. */
export interface StoryTextBlock {
	type?: string | null;
	data?: Record<string, unknown> | null;
}

// Strip storage artifacts from PROSE. The editors persist rendered markup, so paragraph text
// can carry tags and HTML entities. Those are not authored characters — leaving them in would
// send literal `<b>` and `&amp;` to the analyzer, count them toward the min-length gate, and
// distort readability. All three old implementations did this (`cleanText`); dropping it while
// consolidating would have been a silent regression in the opposite direction from TRIL-609.
const cleanProse = (v: unknown): string => {
	if (typeof v !== "string") return "";
	return v
		.replace(/<[^>]*>/g, "")
		.replace(/&nbsp;/g, " ")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/\s+/g, " ")
		.trim();
};

// Code is NOT prose and must not be cleaned. Tag-stripping would corrupt real source —
// `if (a < b) { return c > d; }` contains a `<...>` span that the regex would delete — and
// collapsing whitespace would destroy the line structure. Only trim.
const cleanCode = (v: unknown): string =>
	typeof v === "string" ? v.trim() : "";

const str = cleanProse;

/** First non-empty string among the given keys. */
const firstOf = (
	d: Record<string, unknown> | null | undefined,
	...keys: string[]
): string => {
	for (const k of keys) {
		const v = str(d?.[k]);
		if (v) return v;
	}
	return "";
};

function listText(d: Record<string, unknown> | null | undefined): string {
	const items = d?.items;
	if (!Array.isArray(items)) return "";
	return (
		items
			// No `• ` prefix: the bullet is presentation, not authored text. It inflated the
			// min-length count and put a token in the analyzer's stream that no author typed.
			.map((i) =>
				typeof i === "string" ? str(i) : str((i as { text?: unknown })?.text),
			)
			.filter(Boolean)
			.join("\n")
	);
}

function quoteText(d: Record<string, unknown> | null | undefined): string {
	// `paragraph` FIRST — that is what the editors actually write (TRIL-87 fixed this on the
	// server; the web copy never got the fix, which is TRIL-609). `text`/`quote` are legacy
	// fallbacks for older rows. No surrounding quote marks: synthetic.
	const body = firstOf(d, "paragraph", "text", "quote");
	// The caption is authored, reader-visible text. Every implementation dropped it.
	const caption = str(d?.caption);
	return [body, caption].filter(Boolean).join("\n");
}

function imageText(d: Record<string, unknown> | null | undefined): string {
	// Caption/alt are authored text and belong in the analysis. The `[Image: …]` wrapper does
	// not — it added synthetic characters toward the min-length gate. An image with no caption
	// contributes nothing at all.
	const file = d?.file as { alt?: unknown } | undefined;
	return firstOf(d, "caption", "alt") || str(file?.alt);
}

/** One block's contribution to the analysed text. */
// Real content nests zero levels; this is far beyond any plausible structure. JSON cannot be
// cyclic so there is no true cycle to break, but a pathologically deep chain would blow the
// stack on the path that feeds the SAFETY GATE — cheap insurance for a path we call dead.
const MAX_NESTING_DEPTH = 8;

export function storyTextFromBlock(
	block: StoryTextBlock | null | undefined,
	depth = 0,
): string {
	const direct = ownText(block);
	if (direct) return direct;
	if (depth >= MAX_NESTING_DEPTH) return "";
	// Both pre-TRIL-608 tiers recursed into a nested `block.blocks` when the block carried no
	// text of its own, and `TextBlock` still declares `blocks?: Blocks[]`. Nothing observed
	// writes it, so this is probably dead — but "probably dead" is not a reason to drop a path
	// that feeds the SAFETY GATE. A silent omission here is precisely the failure this module
	// exists to prevent (TRIL-609). Kept until the interface field is removed and it is provably
	// unreachable rather than merely unobserved.
	const nested = (block as { blocks?: unknown })?.blocks;
	return Array.isArray(nested)
		? storyTextFromBlocks(nested as StoryTextBlock[], depth + 1)
		: "";
}

function ownText(block: StoryTextBlock | null | undefined): string {
	const d = block?.data ?? null;
	switch (block?.type) {
		case "Heading":
			return firstOf(d, "header", "text");
		case "List":
			return listText(d);
		case "Quote":
			return quoteText(d);
		case "Code":
			// Full and verbatim — no truncation, no wrapper, and NOT prose-cleaned. See above.
			return cleanCode(d?.code);
		case "Image":
			return imageText(d);
		case "Paragraph":
			return firstOf(d, "paragraph");
		default:
			// Unknown/legacy block: take any plausible authored field rather than dropping it.
			// Dropping is the failure mode this whole module exists to prevent. `code` is checked
			// last and via cleanCode — running source through the prose cleaner would corrupt it.
			return (
				firstOf(d, "paragraph", "text", "content", "header", "title") ||
				cleanCode(d?.code)
			);
	}
}

/**
 * The exact string sent to the story analyzer, counted by the min-length gate, and stored as
 * TRIL-604's `lastAnalyzedText`. All three must agree, which is why this is one function.
 */
export function storyTextFromBlocks(
	blocks: ReadonlyArray<StoryTextBlock> | null | undefined,
	depth = 0,
): string {
	if (!Array.isArray(blocks)) return "";
	return blocks
		.map((b) => storyTextFromBlock(b, depth))
		.filter((t) => t.length > 0)
		.join("\n\n")
		.trim();
}
